'use client'

import React, { useState, useCallback } from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { BirhausSpinner } from '@birhaus/loading'
import { CreditCard, Smartphone, Building2, DollarSign, AlertTriangle } from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import type { 
  BirhausPaymentFormProps, 
  PaymentFormData, 
  PaymentMethod,
  PaymentMethodInfo 
} from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Default payment method configurations
const defaultPaymentMethods: Record<PaymentMethod, PaymentMethodInfo> = {
  efectivo: {
    tipo: 'efectivo',
    nombreEs: 'Efectivo',
    nombreEn: 'Cash',
    icono: <DollarSign size={20} />,
    requiereVerificacion: false,
    limiteTransaccion: 50000000 // 50M PYG SEPRELAD limit
  },
  tarjeta_debito: {
    tipo: 'tarjeta_debito',
    nombreEs: 'Tarjeta de Débito',
    nombreEn: 'Debit Card',
    icono: <CreditCard size={20} />,
    requiereVerificacion: true,
    limiteTransaccion: 10000000, // 10M PYG daily limit
    comision: 0,
    tiempoProcesamientoMinutos: 5
  },
  tarjeta_credito: {
    tipo: 'tarjeta_credito',
    nombreEs: 'Tarjeta de Crédito',
    nombreEn: 'Credit Card',
    icono: <CreditCard size={20} />,
    requiereVerificacion: true,
    limiteTransaccion: 20000000, // 20M PYG
    comision: 2.5, // 2.5% commission
    tiempoProcesamientoMinutos: 10
  },
  transferencia: {
    tipo: 'transferencia',
    nombreEs: 'Transferencia Bancaria',
    nombreEn: 'Bank Transfer',
    icono: <Building2 size={20} />,
    requiereVerificacion: true,
    limiteTransaccion: 100000000, // 100M PYG
    comision: 1000, // Fixed commission
    tiempoProcesamientoMinutos: 60
  },
  billetera_digital: {
    tipo: 'billetera_digital',
    nombreEs: 'Billetera Digital',
    nombreEn: 'Digital Wallet',
    icono: <Smartphone size={20} />,
    requiereVerificacion: true,
    limiteTransaccion: 5000000, // 5M PYG
    comision: 0.5, // 0.5% commission
    tiempoProcesamientoMinutos: 2
  }
}

/**
 * BirhausPaymentForm - Payment form component
 * 
 * Features:
 * - SEPRELAD compliant payment processing
 * - Multiple payment method support
 * - Real-time validation and limits checking
 * - Spanish-first labels and messages
 * - Accessibility compliant form structure
 * - Progressive disclosure of payment details
 * - Commission and processing time display
 */
export function BirhausPaymentForm({
  availablePaymentMethods,
  onPaymentSubmit,
  amount,
  currency = 'PYG',
  recipientInfo,
  className,
  ...props
}: BirhausPaymentFormProps) {
  const { language, reportViolation } = useBirhaus()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null)
  const [formData, setFormData] = useState<Partial<PaymentFormData>>({
    monto: amount,
    moneda: currency,
    destinatario: recipientInfo ? {
      nombre: recipientInfo.nombreEs,
      documento: recipientInfo.documento,
      cuenta: recipientInfo.cuenta
    } : undefined
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // BIRHAUS validation: Check cognitive load (max 7 payment methods - Miller's Law)
  React.useEffect(() => {
    if (availablePaymentMethods.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('form'),
        message: `Payment form has ${availablePaymentMethods.length} payment methods (max recommended: 7)`,
        messageEs: `Formulario de pago con ${availablePaymentMethods.length} métodos de pago (máximo recomendado: 7)`,
        messageEn: `Payment form has ${availablePaymentMethods.length} payment methods (max recommended: 7)`,
        recommendation: 'Group payment methods or use progressive disclosure to reduce cognitive load',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [availablePaymentMethods.length, reportViolation])

  // Get payment method info
  const getPaymentMethodInfo = useCallback((method: PaymentMethod): PaymentMethodInfo => {
    return availablePaymentMethods.find(pm => pm.tipo === method) || defaultPaymentMethods[method]
  }, [availablePaymentMethods])

  // Validate form data
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}

    if (!selectedMethod) {
      newErrors.metodo = language === 'es' 
        ? 'Selecciona un método de pago'
        : 'Select a payment method'
    }

    if (!formData.destinatario?.nombre?.trim()) {
      newErrors.destinatario = language === 'es'
        ? 'Ingresa el nombre del destinatario'
        : 'Enter recipient name'
    }

    if (!formData.destinatario?.documento?.trim()) {
      newErrors.documento = language === 'es'
        ? 'Ingresa el documento del destinatario'
        : 'Enter recipient document'
    }

    if (!formData.concepto?.trim()) {
      newErrors.concepto = language === 'es'
        ? 'Ingresa el concepto del pago'
        : 'Enter payment concept'
    }

    // Check payment method limits
    if (selectedMethod) {
      const methodInfo = getPaymentMethodInfo(selectedMethod)
      if (methodInfo.limiteTransaccion && amount > methodInfo.limiteTransaccion) {
        newErrors.monto = language === 'es'
          ? `Monto excede límite de ${formatCurrency(methodInfo.limiteTransaccion, { currency })}`
          : `Amount exceeds limit of ${formatCurrency(methodInfo.limiteTransaccion, { currency })}`
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [selectedMethod, formData, amount, currency, language, getPaymentMethodInfo])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !selectedMethod) return

    setIsSubmitting(true)
    try {
      const submitData: PaymentFormData = {
        metodo: selectedMethod,
        monto: amount,
        moneda: currency,
        destinatario: {
          nombre: formData.destinatario!.nombre!,
          documento: formData.destinatario!.documento!,
          cuenta: formData.destinatario?.cuenta
        },
        concepto: formData.concepto!,
        programada: formData.programada,
        fechaProgramada: formData.fechaProgramada
      }

      await onPaymentSubmit(submitData)
    } catch (error) {
      console.error('Payment submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [validateForm, selectedMethod, amount, currency, formData, onPaymentSubmit])

  // Update form data
  const updateFormData = useCallback((updates: Partial<PaymentFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear related errors
    if (updates.destinatario) {
      const newErrors = { ...errors }
      delete newErrors.destinatario
      delete newErrors.documento
      setErrors(newErrors)
    }
  }, [errors])

  return (
    <form 
      className={cn('space-y-6', className)}
      onSubmit={handleSubmit}
      {...props}
    >
      {/* Amount Display */}
      <div className="bg-birhaus-muted/30 p-4 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-birhaus-muted-foreground">
            {language === 'es' ? 'Monto a pagar' : 'Amount to pay'}
          </p>
          <p className="text-2xl font-bold text-birhaus-foreground">
            {formatCurrency(amount, { currency, showSymbol: true })}
          </p>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-birhaus-foreground">
          {language === 'es' ? 'Método de pago' : 'Payment method'}
        </label>
        
        <div className="grid gap-3">
          {availablePaymentMethods.map((method) => {
            const isSelected = selectedMethod === method.tipo
            const methodInfo = getPaymentMethodInfo(method.tipo)
            const displayName = language === 'es' ? method.nombreEs : (method.nombreEn || method.nombreEs)
            
            // Check if method can handle the amount
            const exceedsLimit = methodInfo.limiteTransaccion && amount > methodInfo.limiteTransaccion
            
            return (
              <button
                key={method.tipo}
                type="button"
                onClick={() => !exceedsLimit && setSelectedMethod(method.tipo)}
                disabled={!!exceedsLimit}
                className={cn(
                  'p-4 border rounded-lg text-left transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-birhaus-primary',
                  isSelected && 'border-birhaus-primary bg-birhaus-primary/5',
                  !isSelected && !exceedsLimit && 'border-birhaus-border hover:border-birhaus-primary/50',
                  exceedsLimit && 'border-red-200 bg-red-50 opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 text-birhaus-muted-foreground">
                    {method.icono}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-birhaus-foreground">
                        {displayName}
                      </h4>
                      {isSelected && (
                        <div className="w-5 h-5 bg-birhaus-primary rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                    
                    {/* Method details */}
                    <div className="mt-1 text-xs text-birhaus-muted-foreground space-y-1">
                      {methodInfo.comision && (
                        <p>
                          {language === 'es' ? 'Comisión: ' : 'Fee: '}
                          {typeof methodInfo.comision === 'number' && methodInfo.comision < 1
                            ? `${methodInfo.comision}%`
                            : formatCurrency(methodInfo.comision, { currency })}
                        </p>
                      )}
                      
                      {methodInfo.tiempoProcesamientoMinutos && (
                        <p>
                          {language === 'es' ? 'Procesamiento: ' : 'Processing: '}
                          {methodInfo.tiempoProcesamientoMinutos < 60
                            ? `${methodInfo.tiempoProcesamientoMinutos} min`
                            : `${Math.round(methodInfo.tiempoProcesamientoMinutos / 60)}h`}
                        </p>
                      )}
                      
                      {exceedsLimit && (
                        <div className="flex items-center gap-1 text-red-600">
                          <AlertTriangle size={12} />
                          <span>
                            {language === 'es' 
                              ? `Límite: ${formatCurrency(methodInfo.limiteTransaccion!, { currency })}`
                              : `Limit: ${formatCurrency(methodInfo.limiteTransaccion!, { currency })}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
        
        {errors.metodo && (
          <p className="text-sm text-red-600">{errors.metodo}</p>
        )}
      </div>

      {/* Recipient Information */}
      <div className="space-y-4">
        <h3 className="font-medium text-birhaus-foreground">
          {language === 'es' ? 'Información del destinatario' : 'Recipient information'}
        </h3>
        
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-birhaus-foreground mb-1">
              {language === 'es' ? 'Nombre completo' : 'Full name'}
            </label>
            <input
              type="text"
              value={formData.destinatario?.nombre || ''}
              onChange={(e) => updateFormData({
                destinatario: { ...formData.destinatario!, nombre: e.target.value }
              })}
              className={cn(
                'w-full px-3 py-2 border rounded-md',
                'focus:outline-none focus:ring-2 focus:ring-birhaus-primary',
                errors.destinatario ? 'border-red-500' : 'border-birhaus-border'
              )}
              placeholder={language === 'es' ? 'Juan Pérez' : 'John Doe'}
            />
            {errors.destinatario && (
              <p className="mt-1 text-sm text-red-600">{errors.destinatario}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-birhaus-foreground mb-1">
              {language === 'es' ? 'Documento' : 'Document'}
            </label>
            <input
              type="text"
              value={formData.destinatario?.documento || ''}
              onChange={(e) => updateFormData({
                destinatario: { ...formData.destinatario!, documento: e.target.value }
              })}
              className={cn(
                'w-full px-3 py-2 border rounded-md',
                'focus:outline-none focus:ring-2 focus:ring-birhaus-primary',
                errors.documento ? 'border-red-500' : 'border-birhaus-border'
              )}
              placeholder={language === 'es' ? '12345678' : '12345678'}
            />
            {errors.documento && (
              <p className="mt-1 text-sm text-red-600">{errors.documento}</p>
            )}
          </div>
        </div>

        {/* Account number (conditional) */}
        {selectedMethod === 'transferencia' && (
          <div>
            <label className="block text-sm font-medium text-birhaus-foreground mb-1">
              {language === 'es' ? 'Número de cuenta' : 'Account number'}
            </label>
            <input
              type="text"
              value={formData.destinatario?.cuenta || ''}
              onChange={(e) => updateFormData({
                destinatario: { ...formData.destinatario!, cuenta: e.target.value }
              })}
              className="w-full px-3 py-2 border border-birhaus-border rounded-md focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
              placeholder="123456789"
            />
          </div>
        )}
      </div>

      {/* Payment concept */}
      <div>
        <label className="block text-sm font-medium text-birhaus-foreground mb-1">
          {language === 'es' ? 'Concepto' : 'Concept'}
        </label>
        <input
          type="text"
          value={formData.concepto || ''}
          onChange={(e) => updateFormData({ concepto: e.target.value })}
          className={cn(
            'w-full px-3 py-2 border rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-birhaus-primary',
            errors.concepto ? 'border-red-500' : 'border-birhaus-border'
          )}
          placeholder={language === 'es' ? 'Pago de servicios' : 'Service payment'}
        />
        {errors.concepto && (
          <p className="mt-1 text-sm text-red-600">{errors.concepto}</p>
        )}
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting || !selectedMethod}
        className={cn(
          'w-full py-3 px-4 rounded-md font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:ring-offset-2',
          !isSubmitting && selectedMethod
            ? 'bg-birhaus-primary text-white hover:bg-birhaus-primary/90'
            : 'bg-birhaus-muted text-birhaus-muted-foreground cursor-not-allowed'
        )}
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center gap-2">
            <BirhausSpinner size="sm" color="current" />
            <span>{language === 'es' ? 'Procesando...' : 'Processing...'}</span>
          </div>
        ) : (
          <span>
            {language === 'es' ? 'Realizar pago' : 'Make payment'}
            {selectedMethod && (() => {
              const methodInfo = getPaymentMethodInfo(selectedMethod)
              const commission = methodInfo.comision
              if (commission) {
                const commissionAmount = typeof commission === 'number' && commission < 1
                  ? amount * (commission / 100)
                  : commission
                return ` (+ ${formatCurrency(commissionAmount, { currency })})`
              }
              return ''
            })()}
          </span>
        )}
      </button>

      {errors.monto && (
        <p className="text-sm text-red-600 text-center">{errors.monto}</p>
      )}
    </form>
  )
}

export type { BirhausPaymentFormProps, PaymentFormData }